import lib from "@/lib";
import axios from "axios";

export default class AuthModel {
	constructor() {
		this.userProfile = null;
		this.authChecked = false;
		this.mode = "jwt";

		// При инициализации сразу достаем токены из памяти
		this.accessToken = localStorage.getItem("access_token");
		this.refreshToken = localStorage.getItem("refresh_token");
		this.deviceId = localStorage.getItem("device_id");

		// Если токен есть, сразу «заряжаем» axios
		if (this.accessToken) {
			this.setAxiosHeader(this.accessToken);
		}
	}

	// Вспомогательный метод для заголовков
	setAxiosHeader(token) {
		axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
	}

	/**
	 * Инициализация данных пользователя
	 */
	async doInitData() {
		if (this.authChecked) return { success: true };

		// Запускаем оба запроса одновременно
		const [resMe, resMode] = await Promise.allSettled([
			axios.get("/api/me", { withCredentials: true }),
			axios.get("/api/auth/mode", { withCredentials: true }),
		]);

		if (resMe.status === "fulfilled") {
			this.userProfile = resMe.value.data;
			this.authChecked = true;
		} else {
			this.userProfile = null;
			console.warn("User not authorized (401)");
		}

		if (resMode.status === "fulfilled") {
			this.mode = resMode.value.data?.mode || 'jwt';
		} else {
			console.error("Failed to load auth mode", resMode.reason);
			this.mode = "jwt"; // Резервное значение
		}

		return { success: true };
	}

	/**
	 * Авторизация
	 */
	async doLogin(login, password) {
		const data = await lib.request("/api/login", "POST", {
			login,
			password,
		});
		
		if (data.success === false) {
			this.doLogout();
			return {
				success: false,
				message: data.message || "Ошибка входа",
			};
		}

		// Сохраняем в модель
		this.accessToken = data.access_token;
		this.refreshToken = data.refresh_token;
		this.deviceId = data.device_id;

		// Сохраняем в браузер (WSL/Nginx не потеряют это при F5)
		localStorage.setItem("access_token", data.access_token);
		localStorage.setItem("refresh_token", data.refresh_token);
		localStorage.setItem("device_id", data.device_id);

		// Обновляем axios
		this.setAxiosHeader(this.accessToken);

		this.authChecked = false; // Чтобы initData перекачал профиль
		await this.doInitData();

		return { success: true };
	}

	/**
	 * Деавторизация
	 */
	doLogout() {
		this.userProfile = null;
		this.accessToken = null;
		this.authChecked = false;
		localStorage.removeItem("access_token");
		localStorage.removeItem("refresh_token");
		delete axios.defaults.headers.common["Authorization"];
		return true;
	}

	/**
	 * Регистрация нового пользователя
	 * @param {string} login - Логин (уже отфильтрованный на фронте)
	 * @param {string} password - Пароль
	 * @returns {Promise<{success: boolean, message: string, data?: any}>}
	 */
	async doRegister(login, password) {
		try {
			// Отправляем POST запрос на ваш эндпоинт регистрации
			const response = await lib.request("/api/register", "POST", {
				login: login.trim(),
				password: password,
			});

			// Проверяем успешность ответа от сервера
			// (Логика зависит от того, что возвращает ваш бэкенд)
			if (response.success) {
				return {
					success: true,
					data: response.data, // если сервер возвращает данные юзера
				};
			} else {
				return {
					success: false,
					message: response.message || "Ошибка регистрации",
				};
			}
		} catch (error) {
			// Обработка системных ошибок (404, 500, проблемы с сетью)
			console.error("Register error:", error);

			return {
				success: false,
				message: error.response?.data?.message || "Сервер недоступен",
			};
		}
	}

	isAuth() {
		return !!this.userProfile || !!localStorage.getItem("access_token");
	}

	isAdmin() {
		return !!this.userProfile?.is_admin;
	}

	getUserId() {
		return this.userProfile?.id ?? null;
	}

	getLogin() {
		return this.userProfile?.login || "";
	}

	getAvatar() {
		const avatar = this.userProfile?.avatar;
		if (!avatar) return "/files/avatar/default.svg";
		return `/files/avatar/${avatar}`;
	}
}
